import React, { useMemo } from "react";

interface StackedBarSegment {
    label: string;
    value: number;
    testId?: string;
    onClick?: () => void;
}

interface MetricsStackedBarProps {
    label: string;
    segments: StackedBarSegment[];
}

const MetricsStackedBar: React.FC<MetricsStackedBarProps> = ({ label, segments }) => {
    const nonZeroSegments = useMemo(
        () => segments.filter((s) => s.value > 0),
        [segments]
    );

    const total = useMemo(
        () => nonZeroSegments.reduce((sum, s) => sum + s.value, 0),
        [nonZeroSegments]
    );

    // Alternate labels top/bottom so they never overlap
    const labelledSegments = useMemo(
        () =>
            nonZeroSegments.map((segment, index) => ({
                ...segment,
                labelPosition: index % 2 === 0 ? ("top" as const) : ("bottom" as const),
            })),
        [nonZeroSegments]
    );

    // Hybrid width strategy: a fixed baseline per segment guarantees visibility,
    // and the remaining budget is split proportionally so smaller values still
    // scale relative to each other (e.g. 6 appears wider than 1).
    const BASELINE_PERCENT = 4;

    const segmentWidths = useMemo(() => {
        const count = labelledSegments.length;
        if (total === 0 || count === 0) return [];

        const baselineTotal = count * BASELINE_PERCENT;
        const proportionalBudget = Math.max(100 - baselineTotal, 0);

        return labelledSegments.map(
            (s) => BASELINE_PERCENT + (s.value / total) * proportionalBudget
        );
    }, [labelledSegments, total]);

    const allZero = total === 0;

    return (
        <div className={`metrics-stacked-bar${allZero ? " metrics-stacked-bar--empty" : ""}`}>
            <div className="metrics-stacked-bar__label">{label}</div>
            <div className="metrics-stacked-bar__bar-container">
                <div className="metrics-stacked-bar__bar">
                    {allZero
                        ? segments.map((segment, index) => (
                            <div
                                key={index}
                                className="metrics-stacked-bar__segment-wrapper"
                                style={{ width: "20%" }}
                            >
                                <div className="metrics-stacked-bar__segment-label metrics-stacked-bar__segment-label--top">
                                    &nbsp;
                                </div>
                                <div className="metrics-stacked-bar__segment">
                                    <span className="metrics-stacked-bar__segment-value">&nbsp;</span>
                                </div>
                                <div className="metrics-stacked-bar__segment-label metrics-stacked-bar__segment-label--bottom">
                                    &nbsp;
                                </div>
                            </div>
                        ))
                        : labelledSegments.map((segment, index) => {
                            const widthPercent = segmentWidths[index];

                            return (
                              <div
                                key={index}
                                className={`metrics-stacked-bar__segment-wrapper${segment.onClick ? " metrics-stacked-bar__segment-wrapper--clickable" : ""}`}
                                style={{ width: `${widthPercent}%` }}
                                onClick={segment.onClick}
                                data-testid={segment.testId}
                              >
                                {segment.labelPosition === "top" && (
                                  <div
                                    className="metrics-stacked-bar__segment-label metrics-stacked-bar__segment-label--top">
                                    {segment.label}
                                  </div>
                                )}
                                <div className="metrics-stacked-bar__segment">
                                        <span className="metrics-stacked-bar__segment-value">
                                            {segment.value}
                                        </span>
                                </div>
                                {segment.labelPosition === "bottom" && (
                                  <div
                                    className="metrics-stacked-bar__segment-label metrics-stacked-bar__segment-label--bottom">
                                    {segment.label}
                                  </div>
                                )}
                              </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export default MetricsStackedBar;

